import { useCallback, useMemo, useState } from 'react';

import { useQueries } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  type ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';

import { stripPageParam, useAdvanceFilters, type UseAdvanceFiltersReturn } from '@/_containers';
import { usePermissions } from '@/_providers';
import { useBulkDelete } from '@/_shared';

import { invoiceBatchAdvFilters } from './components';
import { invoiceBatchesMainListDefaultApiFilter } from './invoice-batches-main-list-default-api-filter';
import { tableConverter, type TableReturnType } from './table-converter';
import { useDefineColumns } from './use-define-columns';

import { invalidateQueryAllInvoicesAndBatchObjects } from '../shared';
import type { InvoiceBatchFilterSchema } from '../shared/filters';
import {
  getInvoiceBatchForTableQuery,
  useDeleteMutation,
  useGetInvoiceBatchesInfinite,
} from '../shared/invoice-batch-apis';
import type { InvoiceEmailPageCallbacks } from '../shared/invoice-email-page-callbacks';

export function useMainBatchInvoicesPage(
  apiCtrl?: UseAdvanceFiltersReturn<InvoiceBatchFilterSchema>,
  emailCallbacks?: InvoiceEmailPageCallbacks
) {
  const [rowSelection, setRowSelection] = useState({});
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const localAPICtrl = useAdvanceFilters(invoiceBatchAdvFilters, invoiceBatchesMainListDefaultApiFilter);

  const filters = apiCtrl || localAPICtrl;
  const { sorting, page, pagination, searching, filter, createAPIParamStr, mergeFilters, advFilterStr } = filters;

  const apiParamStr = useMemo(() => {
    return createAPIParamStr({
      page,
      filters: filter,
      sortStr: sorting.sort,
      perPage: pagination.per_page,
      searchStr: searching.search_str,
    });
  }, [createAPIParamStr, filter, page, pagination.per_page, searching.search_str, sorting.sort]);
  const mergedAdvFilters = mergeFilters(apiParamStr, advFilterStr);

  const infiniteParamStr = useMemo(() => stripPageParam(mergedAdvFilters), [mergedAdvFilters]);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetInvoiceBatchesInfinite(infiniteParamStr);

  const allBatches = useMemo(() => data.pages.flatMap((p) => p.data), [data.pages]);
  const total = data.pages[0]?.meta?.total ?? 0;

  const { currentModelPermissions } = usePermissions();

  const expandedBatchIds = useMemo(() => {
    if (expanded === true) return allBatches.map((b) => String(b.id));
    if (!expanded) return [];
    return Object.entries(expanded)
      .filter(([, v]) => v)
      .map(([id]) => id);
  }, [expanded, allBatches]);

  const { loadedBatchInvoices, loadingBatchIds } = useQueries({
    queries: expandedBatchIds.map((id) => getInvoiceBatchForTableQuery(id)),
    combine: (results) => ({
      loadedBatchInvoices: Object.fromEntries(
        expandedBatchIds.map((id, i) => [id, results[i]?.data?.data.invoices ?? []])
      ),
      loadingBatchIds: new Set(expandedBatchIds.filter((_, i) => results[i]?.isPending)),
    }),
  });

  const columns = useDefineColumns(sorting, loadingBatchIds, emailCallbacks);

  const cData = useMemo(() => {
    return tableConverter(allBatches, loadedBatchInvoices);
  }, [allBatches, loadedBatchInvoices]);

  const table = useReactTable({
    data: cData,
    columns,

    getRowId: (row) => String(row.id),
    onExpandedChange: setExpanded,
    getSubRows: (row: TableReturnType) => ('invoices' in row ? row.invoices : []),
    getRowCanExpand: (row) => Boolean(row.original.isBatch),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),

    state: {
      columnVisibility,
      rowSelection,
      expanded,
    },

    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
  });

  const bulkDelete = useBulkDelete({
    deleteMutation: useDeleteMutation(),
    invalidateQueryAllObject: () => {
      invalidateQueryAllInvoicesAndBatchObjects();
    },
    resetSelection: table.resetRowSelection,
  });

  const nav = useNavigate();

  const handleFocusEnter = useCallback(
    (row: { id: string | number }) => {
      const id = String(row.id);
      nav({ to: `/invoices/invoice-batch/$id`, params: { id } });
    },
    [nav]
  );

  const handleOnSingleValueResult = useCallback(() => {
    if (allBatches.length === 1) {
      nav({
        to: '/invoices/invoice-batch/$id',
        params: { id: `${allBatches[0].id}` },
      });
    }
  }, [allBatches, nav]);

  return {
    ...bulkDelete,
    ...{ ...filters, advFilterStr: mergedAdvFilters },
    table,
    allBatches,
    total,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    currentModelPermissions,
    handleFocusEnter,
    handleOnSingleValueResult,
    loadedBatchInvoices,
  };
}
